<script setup>
import { ref, computed, watch } from 'vue'
import Bar from '@/components/common/dashboard/chart/Bar.vue'
import Doughnut from '@/components/common/dashboard/chart/Doughnut.vue'

import { getTotalItemsRevGMSalesUserList, getTotalRevGMList, getTotalItemsRevGMList } from '@/api/dashboard'
import { defaultOptions, FCSTMapper, getThisYear } from '@/js/customChart'

import { useNavigatorStore } from "@/store/navigator"
const navigatorStore = useNavigatorStore()

// props
const props = defineProps({
  chartType: String,
  employeeId: String,
  legend: String,
  width: Number,
  height: Number,
  title: String,
  data: Object,
  fiscalYear:String
})

// data
const fetchedData = ref()

const APIS = {
  chief: getChief,
  stage: getStage,
  goods: getGoods,
}

// computed
const getChartType = computed(() => {
  const charts = {
    vertical: Bar,
    horizontal: Bar,
    doughnut: Doughnut,
  }
  return charts[props.chartType]
})

const getOptions = computed(() => {
  if (props.chartType !== 'doughnut') {
    return {
      aspectRatio: navigatorStore.isMobile ? 1.2 : 2,
      ...defaultOptions[props.chartType]
    }
  }
  return defaultOptions[props.chartType]
})
watch([() => props.chartType, ()=>props.legend], (newValue, oldValue) => {
  if (newValue && props.legend &&  props.chartType,fetchedData.value) {
    if(props.chartType==='vertical'||props.chartType === 'horizontal'){
      fetchedData.value.data.datasets[0].data = fetchedData.value.additionalData.realData
    }else{
      fetchedData.value.data.datasets[0].data = fetchedData.value.additionalData.data
    }
    
  }
})
// watch
watch([() => props.employeeId, () => props.legend, ()=>props.fiscalYear], newValue => {
  const [employeeId, legend, fiscalYear] = newValue
  try {
    if(legend){
      APIS[legend](employeeId,fiscalYear)
    }
    
  } catch (err) {
    console.error(err)
  }
}, { immediate: true })



async function getChief(employeeId,fiscalYear) {
  try {
    const result = await getTotalItemsRevGMSalesUserList({
      fs_year: fiscalYear,
      employee_id: employeeId,
    })
    const labels = Object.keys(
      Object.groupBy(
        result.rtn.filter(item => item.title === 'REV')
        .sort((a, b) => a.sortord - b.sortord), 
        item => item.itemname
      ))
    const { data, rawData, dataPer, centerValue,realData } = FCSTMapper(result, 'REV', labels)
    fetchedData.value = {
      data: {
        labels,
        datasets: [
          {
            data:props.chartType == 'doughnut'? data:rawData,
            backgroundColor: [
              "rgba(54, 162, 235, 0.8)",
              "rgba(255, 206, 86, 0.8)",
              "rgba(200, 200, 200, 0.8)"
            ],
            cutout: '70%',
          }
        ]
      },
      additionalData: {
        rawData,
        dataPer,
        centerValue,
        data,
        realData,
        key : 'rev'
      }
    }
    if(props.chartType==='vertical'||props.chartType === 'horizontal'){
      fetchedData.value.data.datasets[0].data = fetchedData.value.additionalData.realData
    }else{
      fetchedData.value.data.datasets[0].data = fetchedData.value.additionalData.data
    }
  } catch (err) {
    console.error(err)
  }
}

async function getStage(employeeId,fiscalYear) {
  try {
    const result = await getTotalRevGMList({
      fs_year: fiscalYear,
      employee_id: employeeId,
    })
    const labels = ["Commit", "Won", "Close"]
    const { data, rawData, dataPer, centerValue, realData } = FCSTMapper(result, 'REV', labels)
    fetchedData.value = {
      data: {
        labels,
        datasets: [
          {
            data:props.chartType == 'doughnut'? data:rawData,
            backgroundColor: [
              "rgba(54, 162, 235, 0.8)",
              "rgba(255, 206, 86, 0.8)",
              "rgba(200, 200, 200, 0.8)"
            ],
            cutout: '70%'
          }
        ]
      },
      additionalData: {
        rawData,
        dataPer,
        centerValue,
        data,
        realData,
        key : 'rev'
      }
    }
    if(props.chartType==='vertical'||props.chartType === 'horizontal'){
      fetchedData.value.data.datasets[0].data = fetchedData.value.additionalData.realData
    }else{
      fetchedData.value.data.datasets[0].data = fetchedData.value.additionalData.data
    }
  } catch (err) {
    console.error(err)
  }
}

async function getGoods(employeeId,fiscalYear) {
  try {
    const result = await getTotalItemsRevGMList({
      fs_year: fiscalYear,
      employee_id: employeeId,
    })
    const labels = ["H/W", "S/W", "SVC", "ETC"]
    const { data, rawData, dataPer, centerValue, realData } = FCSTMapper(result, 'REV', labels)
    fetchedData.value = {
      data: {
        labels,
        datasets: [
          {
            data:props.chartType == 'doughnut'? data:rawData,
            backgroundColor: [
              "rgba(54, 162, 235, 0.8)",
              "rgba(255, 100, 86, 0.8)",
              "rgba(255, 206, 86, 0.8)",
              "rgba(200, 200, 200, 0.8)"
            ],
            cutout: '70%'
          }
        ]
      },
      additionalData: {
        rawData,
        dataPer,
        centerValue,
        data,
        realData,
        key : 'rev'
      }
    }
    if(props.chartType==='vertical'||props.chartType === 'horizontal'){
      fetchedData.value.data.datasets[0].data = fetchedData.value.additionalData.realData
    }else{
      fetchedData.value.data.datasets[0].data = fetchedData.value.additionalData.data
    }
  } catch (err) {
    console.error(err)
  }
}
</script>

<template>
  <component :is='getChartType' :width="width" :height="height" :data="fetchedData?.data"
    :additionalData="fetchedData?.additionalData" :title="title" :options="getOptions" :type ="props.chartType"/>
</template>